import { faPen, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import {
    faPen as faPenSolid,
    faTrash as faTrashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { IAssetCategoryResponse } from '@kcftech/prometheus-client';
import { HighlightedAssetCategoryName } from 'components/asset_classification/HighlightedAssetCategoryName';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { Tag } from 'components/tag/Tag';
import { usePermissions } from 'contexts/PermissionsContext';
import {
    TreeItemActions,
    TreeItemBar,
    TreeItemLabel,
} from 'pages/Settings/AssetType/styled';
import { CategoryTreeData } from 'pages/Settings/AssetType/types';
import { TreeNode } from 'shared/data_tree/types';
import { DebugValue } from 'shared/debug/DebugValue';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

type CategoryLabelProps = Readonly<{
    $isSelected: boolean;
}>;

const maxCategoryDepth = 8;

const CategoryLabel = styled.span<CategoryLabelProps>`
    background: ${({ $isSelected }) =>
        $isSelected ? tokens.action.selected : 'transparent'};
`;

export type AssetCategoryDetailTreeNodeProps = Readonly<{
    node: TreeNode<CategoryTreeData>;
    selectedCategory: IAssetCategoryResponse | undefined;
    filterText: string;
    onCategoryCreateClick: () => void;
    onCategoryEditClick: () => void;
    onCategoryDeleteClick: () => void;
}>;

export function AssetCategoryDetailTreeNode({
    node,
    selectedCategory,
    filterText,
    onCategoryCreateClick,
    onCategoryEditClick,
    onCategoryDeleteClick,
}: AssetCategoryDetailTreeNodeProps) {
    const { canEditAssetType } = usePermissions();
    const { category } = node.data;

    const isSelected =
        selectedCategory !== undefined &&
        category !== undefined &&
        category.Id === selectedCategory.Id;

    const canAddCategory = node.level < maxCategoryDepth;

    return (
        <TreeItemBar className="f-14">
            {category ? (
                <TreeItemLabel>
                    <CategoryLabel $isSelected={isSelected}>
                        <HighlightedAssetCategoryName
                            category={category}
                            filterText={filterText}
                        />
                    </CategoryLabel>
                    <DebugValue value={category} description="Asset Category" />
                    {category.ParameterCount > 0 && (
                        <span
                            title={`${category.ParameterCount} parameters`}
                            className="ml-10"
                        >
                            <Tag
                                style={{
                                    backgroundColor: tokens.surface.secondary,
                                    height: '2rem',
                                }}
                            >
                                {category.ParameterCount}
                            </Tag>
                        </span>
                    )}
                </TreeItemLabel>
            ) : (
                <TreeItemLabel>Categories</TreeItemLabel>
            )}
            {canEditAssetType && (
                <TreeItemActions>
                    {canAddCategory && (
                        <HoverIconButton
                            ariaLabel={
                                category
                                    ? `create category below ${category.Name}`
                                    : 'create category button'
                            }
                            icon={faPlus}
                            hoverIcon={faPlus}
                            color={tokens.icon.actionActive}
                            hoverColor={tokens.icon.actionHover}
                            size="14"
                            onClick={(event) => {
                                onCategoryCreateClick();
                                event.stopPropagation();
                            }}
                        />
                    )}
                    {category && (
                        <>
                            <HoverIconButton
                                ariaLabel={`edit ${category.Name} category button`}
                                icon={faPen}
                                hoverIcon={faPenSolid}
                                color={tokens.icon.actionActive}
                                hoverColor={tokens.icon.actionHover}
                                size="14"
                                onClick={(event) => {
                                    onCategoryEditClick();
                                    event.stopPropagation();
                                }}
                            />
                            <HoverIconButton
                                ariaLabel={`delete ${category.Name} category button`}
                                icon={faTrash}
                                hoverIcon={faTrashSolid}
                                color={tokens.icon.actionActive}
                                hoverColor={tokens.error.default}
                                size="14"
                                onClick={(event) => {
                                    onCategoryDeleteClick();
                                    event.stopPropagation();
                                }}
                            />
                        </>
                    )}
                </TreeItemActions>
            )}
        </TreeItemBar>
    );
}
